import React, { useEffect, useState } from "react";
import "./CohortList.scss";
import SuggestedCohort from "./SuggestedCohort";
import CustomCohort from "./CustomCohort";
import { useGetCohortDetailsListQuery } from "../../../../rtk-query/features/cohort/cohortRTK";
import {
  setAssetSize,
  setClearAll,
  setCohortApplied,
  setIsEditCohort,
  setIsFieldsEditiable,
  setMode,
  setResetSearchTextForAdditionalInstitutions,
  setResetSearchTextForCities,
  setSelectedAdditionalInstitutions,
  setSelectedCities,
  setSelectedCohort,
  setSelectedInstitutions,
} from "../../../../rtk-query/features/cohort/cohortSlice";
import { useDispatch, useSelector } from "react-redux";
import { FiSearch } from "react-icons/fi";
import { Skeleton } from "primereact/skeleton";
import useFetch from "../../../../rtk-query/features/useFetch";
import { PRIVILEGES } from "../../../../utils/privileges";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSubscription } from "../../../../hooks/useSubscription";

export const COHORT_MODE = {
  SUGGESTED: "suggested",
  CUSTOM: "custom",
};

const SKELETON_LIST_COUNT = 2;
const SKELETON_KEYS = Array.from({ length: SKELETON_LIST_COUNT }, (_, i) => `skeleton-card-${i}`);

const flattenObject = (obj) => {
  let result = "";
  for (const key in obj) {
    const value = obj[key];
    if (value === null || value === undefined) continue;
    result += typeof value === "object"
      ? ` ${flattenObject(value)}`
      : ` ${String(value)}`;
  }
  return result;
};

const deepSearch = (list, searchText) => {
  const q = searchText.toLowerCase();
  return list?.filter((item) => flattenObject(item).toLowerCase().includes(q));
};

const CohortList = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const { hasSubscription } = useSubscription();
  const [searchText, setSearchText] = useState("");
  const [filteredCohort, setFilteredCohort] = useState([]);
  const [isCustomLoading, setIsCustomLoading] = useState(false);
  const idrssd = useSelector((state) => state?.orgReducer?.selectedFI?.idrssd);

  const mode = useSelector((state) => state?.cohortReducer?.mode);
  const selectedCohort = useSelector((state) => state?.cohortReducer?.selectedCohort);
  const resetSearchTextForAdditionalInstitutions = useSelector(
    (state) => state?.cohortReducer?.resetSearchTextForAdditionalInstitutions,
  );

  const {
    data: cohortList,
    isLoading: isLoadingCohortList,
  } = useFetch(useGetCohortDetailsListQuery, { skip: idrssd == null });

  const canCreateCohort = hasPermission(PRIVILEGES.CREATE_COHORT);
  const hasCreateSubscription = hasSubscription(PRIVILEGES.CREATE_COHORT);

  useEffect(() => {
    setIsCustomLoading(true);
  }, [mode]);

  useEffect(() => {
    if (!cohortList?.data?.data?.length || selectedCohort == null) return;
    const match = cohortList.data.data.find((i) => i.uid === selectedCohort?.uid);
    if (match != null) dispatch(setSelectedCohort(match));
  }, [cohortList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFilteredCohort(deepSearch(cohortList?.data?.data, searchText));
    setIsCustomLoading(false);
  }, [cohortList, searchText, mode]);

  useEffect(() => {
    if (!resetSearchTextForAdditionalInstitutions) return;
    setSearchText("");
    dispatch(setResetSearchTextForAdditionalInstitutions(false));
  }, [resetSearchTextForAdditionalInstitutions, dispatch]);

  const handleNewCohort = () => {
    dispatch(setClearAll(true));
    dispatch(setCohortApplied(null));
    dispatch(setSelectedCohort(null));
    dispatch(setSelectedInstitutions([]));
    dispatch(setResetSearchTextForAdditionalInstitutions(true));
    dispatch(setResetSearchTextForCities(true));
    dispatch(setSelectedAdditionalInstitutions([]));
    dispatch(setAssetSize({ minValue: null, maxValue: null }));
    dispatch(setSelectedCities([]));
    dispatch(setIsEditCohort(false));
    dispatch(setIsFieldsEditiable(true));
  };

  const handleTabChange = (tabMode) => {
    dispatch(setMode(tabMode));
    dispatch(setCohortApplied(null));
    dispatch(setResetSearchTextForAdditionalInstitutions(true));
    dispatch(setResetSearchTextForCities(true));
  };

  const handleSearchChange = (e) => setSearchText(e.target.value);

  const handleSuggestedTab = () => handleTabChange(COHORT_MODE.SUGGESTED);
  const handleCustomTab = () => handleTabChange(COHORT_MODE.CUSTOM);

  const renderCohortPanel = () => {
    if (mode === COHORT_MODE.SUGGESTED) return <SuggestedCohort cohortList={filteredCohort} />;
    return <CustomCohort cohortList={filteredCohort} />;
  };

  return (
    <div className="cohort-list">
      <h3>Cohort List</h3>
      <div className="header-section">
        {isLoadingCohortList ? (
          <div className="select-field">
            <Skeleton height="3rem" />
          </div>
        ) : (
          <div className="select-field">
            <FiSearch className="search-icon" />
            <input
              type="text"
              value={searchText}
              className="search-input"
              placeholder="Search cohort by name"
              onChange={handleSearchChange}
            />
          </div>
        )}

        {isLoadingCohortList ? (
          <div className="select-field" style={{ maxWidth: "10rem" }}>
            <Skeleton height="3rem" />
          </div>
        ) : (
          canCreateCohort && (
            <button
              type="button"
              className={`add-btn${hasCreateSubscription ? "" : " disabled"}`}
              disabled={!hasCreateSubscription}
              onClick={handleNewCohort}
            >
              + Cohort
            </button>
          )
        )}
      </div>

      {isLoadingCohortList ? (
        <div className="tabs">
          <div className="select-field">
            <Skeleton height="3rem" />
          </div>
          <div className="select-field">
            <Skeleton height="3rem" />
          </div>
        </div>
      ) : (
        <div className="tabs">
          <button
            type="button"
            className={mode === COHORT_MODE.SUGGESTED ? "active" : ""}
            onClick={handleSuggestedTab}
          >
            Suggested Cohort
          </button>
          <button
            type="button"
            className={mode === COHORT_MODE.CUSTOM ? "active" : ""}
            onClick={handleCustomTab}
          >
            Custom Cohort
          </button>
        </div>
      )}

      {isLoadingCohortList || isCustomLoading ? (
        <div className="list-cohort flex-start">
          {SKELETON_KEYS.map((key) => (
            <div key={key} className="skeleton-card">
              <Skeleton
                width="100%"
                height="241px"
                style={{ marginBottom: "24px" }}
              />
            </div>
          ))}
        </div>
      ) : (
        renderCohortPanel()
      )}
    </div>
  );
};

export default CohortList;
